<template lang="pug">
#other-user-group-member-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者ユーザー情報修正

    // LINEユーザーの編集
    .box-body.register-body(v-if="senderType == 0")
      FormInputSelect(
        key-name="種類・グループ"
        required
        name="group"
        v-model="groupId"
        :options="otherUserGroupsSelectOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '540px' }"
      )
      FormLabel(key-name="氏名" :value="name")
      FormLabel(key-name="所属／係／担当" :value="organization")
      FormLabel(key-name="電話番号" :value="tel")
      .register-row
        .register-group
          .register-key
            span.key-name 個別使用設定（空室／空床情報）
          div
            UsableFunctionSwitch(v-model="otherUserPermittedFunctions.vacancy")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    // メールユーザーの編集
    .box-body.register-body(v-if="senderType == 1")
      FormInputSelect(
        key-name="種類・グループ"
        required
        name="group"
        v-model="groupId"
        :options="otherUserGroupsSelectOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '540px' }"
      )
      FormInput(
        key-name="氏名"
        required
        name="name"
        v-model="name"
        type="text"
        placeholder="氏名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="所属／係／担当"
        name="organization"
        v-model="organization"
        type="text"
        placeholder="所属／係／担当"
        :maxlength="32"
        :validation-rules="{ max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="電話番号"
        required
        name="tel"
        v-model="tel"
        type="text"
        placeholder="電話番号"
        option-text="-（ハイフン）なしで入力してください。"
        :maxlength="11"
        :validation-rules="{ required: true, numeric: true, max: 13, min: 5 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="メールアドレス"
        required
        name="email"
        v-model="email"
        type="text"
        placeholder="メールアドレス"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true, equal_value: confirmEmail }"
        :styles="{ width: '540px' }")
      FormInput(
        key-name="メールアドレス（確認）"
        required
        name="confirm-email"
        v-model="confirmEmail"
        type="text"
        placeholder="メールアドレス（確認）"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true }"
        :styles="{ width: '540px' }")
      .register-row.caution-message
        .register-group.font-strong-color メールアドレスが間違っていないか、登録後に登録確認メッセージを送信し、受信できているかをお電話等でご確認ください。

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoOtherUserGroupList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="施設関係者ユーザー情報を更新しますか？" @ok="updateOtherUserGroupMember")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoOtherUserGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { OtherUserGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel, { OtherUserPermittedFunctions } from "@/commons/models/OtherUserGroup";
import OtherUserGroupMembersService, { OtherUserGroupMembersPatchModel } from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import MailOtherUserGroupMembersService, { MailOtherUserGroupMembersPatchModel } from "@/commons/services/MailOtherUserGroupMembers";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import { SenderType } from "@/commons/models/Resident";
import UsableFunctionSwitch from "@/pc/basics/UsableFunctionSwitch.vue";

/**
 * 施設関係者グループメールメンバー登録画面コンポーネント
 */
@Component({
  components: {
    FormInputSelect,
    FormLabel,
    FormInput,
    UsableFunctionSwitch,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserGroupMemberEdit extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 施設関係者グループメールメンバーサービス */
  @Inject("MailOtherUserGroupMembersService") mailOtherUserGroupMembersService!: MailOtherUserGroupMembersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設関係者グループ選択肢 */
  otherUserGroupsSelectOptions: Array<{ value: number; label: string }> = [];
  /** 送信種別 */
  senderType: number = -1;
  /** 施設関係者グループID */
  groupId: number = 0;
  /** 種類・施設関係者グループ名 */
  groupName: any = "";
  /** 施設関係者グループメンバー名 */
  name: string = "";
  /** 所属／係／担当 */
  organization: string = "";
  /** 電話番号 */
  tel: string = "";
  /** メールアドレス */
  email: string = "";
  /** メールアドレス(確認) */
  confirmEmail: string = "";
  /** 機能使用可否 */
  otherUserPermittedFunctions: OtherUserPermittedFunctions = {
    vacancy: false
  };
  /** 更新日時 */
  updated: number = 0;

  /** APIから返却された場合に表示するエラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 施設関係者グループの種類名変換用に種類名のMAPを作成する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypeMap(otherUserGroupTypes);

    // 施設関係者グループの選択肢を作成する
    const otherUserGroupsSelectOptions: Array<{ value: number; label: string }> = await this.createOtherUserGroupsSelectOptions(otherUserGroupTypeMap);
    this.otherUserGroupsSelectOptions = otherUserGroupsSelectOptions;

    // 初期値を格納する
    const groupId: number = Number(this.$route.params.groupid);
    const memberId: number = Number(this.$route.params.id);
    const otherUserGroupMember: OtherUserGroupModel = await this.getOtherUserGroupMember(groupId, memberId);

    const typeName: string | undefined = otherUserGroupTypeMap.get(otherUserGroupMember.groupTypeId);
    this.senderType = otherUserGroupMember.senderType;
    this.groupId = groupId;
    this.groupName = typeName ? `${typeName}・${otherUserGroupMember.name}` : `${otherUserGroupMember.name}`;
    this.name = otherUserGroupMember.memberName;
    this.organization = otherUserGroupMember.memberOrganization;
    this.tel = otherUserGroupMember.memberTel;
    this.email = otherUserGroupMember.memberMailaddress ? otherUserGroupMember.memberMailaddress : "";
    this.confirmEmail = otherUserGroupMember.memberMailaddress ? otherUserGroupMember.memberMailaddress : "";
    this.otherUserPermittedFunctions = otherUserGroupMember.otherUserPermittedFunctions as OtherUserPermittedFunctions;
    this.updated = otherUserGroupMember.updated;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypeMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### 施設関係者グループの選択肢を作成する
   */
  public async createOtherUserGroupsSelectOptions(otherUserGroupTypeMap: Map<number, string>): Promise<Array<{ value: number; label: string }>> {
    const groups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();

    let selectOptions: Array<{ value: number; label: string }> = [];
    for (const group of groups) {
      const typeName: string | undefined = otherUserGroupTypeMap.get(group.groupTypeId);

      const option: { value: number; label: string } = {
        value: Number(group.id),
        label: typeName ? `${typeName}・${group.name}` : `${group.name}`
      };
      selectOptions.push(option);
    }

    return selectOptions;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 施設関係者グループメンバーを取得する
   *
   * - 施設関係者グループメンバー単一を取得するAPIはないので、施設関係者グループにひもづくメンバーを取得する
   * - 施設関係者グループメンバーの中からメンバーIDが同一のものを修正対象のメンバーと判断する
   * - 見つからなかった場合は404画面に遷移させたいので例外を投げる
   */
  public async getOtherUserGroupMember(groupId: number, memberId: number): Promise<OtherUserGroupModel> {
    const members: Array<OtherUserGroupModel> = await this.otherUserGroupMembersService.get(userStore, groupId);

    for (const member of members) {
      if (member.memberId == memberId) {
        return member;
      }
    }

    let systemError: SystemError = new SystemError(`otherUserGroupMember is notfound. groupId: ${groupId}, memberId: ${memberId}.`);
    systemError.statusCode = 404;
    throw systemError;
  }

  /**
   * ### 確認モーダルを表示する
   *
   * - バリデーターを使用してバリデーションする
   * - バリデーションでエラーが無ければ確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 施設関係者グループ一覧画面へ遷移する
   */
  public gotoOtherUserGroupList(): void {
    this.$router.push(PcUrl.OtherUserGroupList);
    return;
  }

  /**
   * ### 施設関係者グループメールメンバーを更新する
   */
  public async updateOtherUserGroupMember(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      if (this.senderType == SenderType.LINE) {
        await this.updateLineOtherUserGroupMember();
      } else if (this.senderType == SenderType.MAIL) {
        await this.updateMailOtherUserGroupMember();
      }

      // 施設関係者を再読み込みさせるようstoreを更新
      let otherUserGroupList: OtherUserGroupListState = needRefreshesStore.otherUserGroupList;
      otherUserGroupList.otherUserGroupList = true;
      needRefreshesStore.setOtherUserGroupList(otherUserGroupList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 登録成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        if (this.senderType == SenderType.LINE) {
          this.errorMessage = "既に同一のユーザーが登録されているため登録できません。";
        } else if (this.senderType == SenderType.MAIL) {
          this.errorMessage = "既に同一のメールアドレスが登録されているため登録できません。";
        }
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設関係者グループメンバー(LINE)を更新する
   *
   * - グループIDが変更されている場合は施設関係者グループメンバーserviceを使用して更新する
   */
  public async updateLineOtherUserGroupMember(): Promise<void> {
    // グループIDも変更できるようになったので、更新対象のグループIDは変更前のものを使用する
    const groupId: number = Number(this.$route.params.groupid);
    const memberId: number = Number(this.$route.params.id);

    const requestModel: OtherUserGroupMembersPatchModel = this.createLineUserRequestModel();
    await this.otherUserGroupMembersService.patch(userStore, groupId, memberId, requestModel);

    return;
  }

  /**
   * ### 施設関係者グループメールメンバー(LINE)更新用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   */
  public createLineUserRequestModel(): OtherUserGroupMembersPatchModel {
    const requestModel: OtherUserGroupMembersPatchModel = {
      groupId: this.groupId,
      memberName: this.name,
      memberOrganization: this.organization ? this.$stringUtil.convertYen(this.organization) : "入力無し",
      memberTel: this.tel,
      otherUserPermittedFunctions: this.otherUserPermittedFunctions,
      updated: this.updated
    };

    return requestModel;
  }

  /**
   * ### 施設関係者グループメンバー(メール)を更新する
   */
  public async updateMailOtherUserGroupMember(): Promise<void> {
    // グループIDも変更できるようになったので、更新対象のグループIDは変更前のものを使用する
    const groupId: number = Number(this.$route.params.groupid);
    const memberId: number = Number(this.$route.params.id);

    const requestModel: MailOtherUserGroupMembersPatchModel = this.createMailUserRequestModel();
    await this.mailOtherUserGroupMembersService.patch(userStore, groupId, memberId, requestModel);

    return;
  }

  /**
   * ### 施設関係者グループメールメンバー(メール)更新用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   */
  public createMailUserRequestModel(): MailOtherUserGroupMembersPatchModel {
    const requestModel: MailOtherUserGroupMembersPatchModel = {
      groupId: this.groupId,
      memberName: this.name,
      memberOrganization: this.organization ? this.$stringUtil.convertYen(this.organization) : "入力無し",
      memberMailaddress: this.email,
      memberTel: this.tel,
      otherUserPermittedFunctions: this.otherUserPermittedFunctions,
      updated: this.updated
    };

    return requestModel;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#other-user-group-member-edit {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
